import * as React from "react";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow, makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger, shorthands,
  TableCellLayout,
  TableColumnDefinition,
} from "@fluentui/react-components";
import { IDataTableProps, IFilterLocalStorage, IFilterQuery } from "@/DependenciesV9/DataTable/Model.ts";
import { TableColumnSizingOptions } from "@fluentui/react-table";
import { DataTableBaseWrapper } from "@/DependenciesV9/DataTable/Style.ts";
import { FilterType, PAGING, SelectionMode } from "@/DependenciesV9/DataTable/Enums/TableEnum.ts";
import { nameAttribute } from "@/commons/RCAttribute.ts";
import Filter from "@/DependenciesV9/DataTable/Filter";
import { IRefFilter } from "@/DependenciesV9/DataTable/Filter/Model.ts";
import { SearchResponse } from "@/services/entities/AxiosMethod.ts";
import { buildEndpointWithSortAndFilter } from "@/DependenciesV9/DataTable/FunctionTable/Filter.ts";

const useMyStyles = makeStyles({
  root: {
    width: '100%',
    height: '100%',
  },
  dataGrid: {
    width: '100%',
    height: '100%',

    display: 'flex',
    flexDirection: 'column',
    ...shorthands.overflow('hidden'),
  },
  body: {
    ...shorthands.overflow('auto'),
    height: 0,
    flexGrow: 1,
    flexShrink: 1,
  },
});

const DataTable = forwardRef((props: IDataTableProps, ref) => {
  const styles = useMyStyles();
  const refMap = React.useRef<Record<string, HTMLElement | null>>({});
  const refFilter = React.useRef<IRefFilter | null>(null);
  const [columns, setColumns] = useState<TableColumnDefinition<any>[]>([]);
  const [columnSizing, setColumnSizing] = useState<TableColumnSizingOptions>({});
  //const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [skipNumber, setSkipNumber] = useState<number | null>(null);
  const [isDoneListLazyLoad, setIsDoneListLazyLoad] = useState<boolean>(false);
  const [filterQuery, setFilterQuery] = useState<IFilterQuery[]>([]);
  const [, setSortColumn] = useState<IFilterQuery | null>(null);
  const [, setTotal] = useState<number>(0);

  useEffect(() => {
    setColumnsFromProps();
  }, [props.columns]);

  useEffect(() => {
    if (props.isLazyLoad) {
      const nameDiv: string = `HOC-wrapper-lazy-${props.rcName || "list"}`;
      const itemHeight = document.getElementById(nameDiv)?.clientHeight;
      const viewPort = document.getElementById(nameDiv)?.clientWidth;
      let count = 0;
      if (itemHeight && viewPort) {
        count = Math.floor(itemHeight / 43 + 2);
        setSkipNumber(count);
      }
    } else {
      setSkipNumber(props.pageSize || PAGING.Size);
    }
  }, [props.pageSize]);

  useEffect(() => {
    if (skipNumber !== null) {
      const storageData = onHandleGetLocalStorageData();
      const listSorts: IFilterQuery[] = (storageData.filterQuery || []).filter(
        (item: IFilterQuery) => item.type === FilterType.Sort
      );
      if (!listSorts.length && props.defaultSort) {
        listSorts.push({
          childQuery: props.defaultSort?.childQuery || "",
          key: props.defaultSort?.key || "",
          operator: props.defaultSort?.operator || "",
          value: props.defaultSort?.value || "",
          type: FilterType.Sort,
          order: props.defaultSort?.order || "",
        });
      }
      const filterFromLocal: IFilterQuery[] = storageData.filterQuery.filter(
        (item: IFilterQuery) => item.type !== FilterType.Sort
      );
      setFilterQuery(filterFromLocal);
      setSortColumn(listSorts[0]);
      firstLoadData(filterFromLocal || undefined, listSorts[0]);
    }
  }, [skipNumber, page]);

  const firstLoadData = async (
    filterTemp?: IFilterQuery[],
    sortQueryClone?: IFilterQuery | null,
    pageClone?: number
  ): Promise<void> => {
    const skip: number =
      (skipNumber || 0) * (pageClone ? pageClone - 1 : page - 1);
    let endpoint: string = buildEndpointWithSortAndFilter(
      props.columns,
      filterTemp ? filterTemp : filterQuery,
      sortQueryClone || null
    );
    endpoint = endpoint.replace("?", "&");
    const filter: string = `?$top=${skipNumber}${
      skip ? `&$skip=${skip}` : ""
    }${endpoint}`;
    if (props.loadDataTable) {
      if (props.isLazyLoad) {
        const response: SearchResponse<any> = await props.loadDataTable(
          filter,
          true
        );
        if (!props.isHaveNextLinkLazyLoad) {
          if (skipNumber && response.data.length < skipNumber) {
            setIsDoneListLazyLoad(true);
          } else {
            setIsDoneListLazyLoad(false);
          }
        } else {
          setIsDoneListLazyLoad(!response.nextLink);
        }
      } else {
        const response: SearchResponse<any> = await props.loadDataTable(
          filter,
          undefined,
          page,
          skipNumber || undefined
        );
        setTotal(response.total);
      }
    }
  };

  const setColumnsFromProps = (): void => {
    const columnsClone: TableColumnDefinition<any>[] = [];
    const columnSizingClone: TableColumnSizingOptions = {};
    (props.columns || []).forEach(columnDl => {
      const column = createTableColumn<any>({
        columnId: columnDl.key,
        compare: () => {
          return 1;
        },
        renderHeaderCell: () => {
          return columnDl.name;
        },
        renderCell: (data) => {
          return (
            <TableCellLayout truncate>
              {columnDl.onRender ? columnDl.onRender(data) : ""}
            </TableCellLayout>
          );
        },
      });
      columnSizingClone[columnDl.key] = {
        minWidth: columnDl.minWidth || undefined,
        defaultWidth: columnDl.maxWidth && columnDl.minWidth ? (columnDl.maxWidth + columnDl.minWidth) / 2 : undefined,
        idealWidth: columnDl.maxWidth && columnDl.minWidth ? (columnDl.maxWidth + columnDl.minWidth) / 2 : undefined,
        autoFitColumns: true
      }
      columnsClone.push(column);
    })
    setColumns(columnsClone);
    setColumnSizing(columnSizingClone)
  }

  const onHandleScrollList = async (
  ) => {
    const name = props.rcName ? `dl.${props.rcName}` : "dl.list";
    const list: HTMLElement = document.querySelectorAll(
      `[data-rc-id*="${name}"]`
    )[0] as HTMLElement;
    if(
      list &&
      Math.floor(list.scrollTop) >= list.scrollHeight - list.clientHeight &&
      Math.floor(list.scrollTop) !== 0 &&
      props.isLazyLoad &&
      !isDoneListLazyLoad
    ) {
      console.log("scrolling");
      const pageClone = page + 1;
      setPage(pageClone);
    }
  };

  const onOpenFilter = () => {
    if(refFilter?.current?.onOpenFilter) {
      refFilter.current?.onOpenFilter(filterQuery);
    }
  }

  const onHandleGetLocalStorageData = (): {
    filterQuery: IFilterQuery[];
  } => {
    const listData = localStorage.getItem("listData");
    if (listData) {
      const data: IFilterLocalStorage[] = JSON.parse(listData);
      const listName = props.rcName;
      const listItem = data.find((i: any) => i.listName === listName);
      if (listItem) {
        return {
          filterQuery: listItem.filterQuery,
        };
      }
    }
    return { filterQuery: [] };
  };

  useImperativeHandle(ref, () => ({
    onOpenFilter,
  }));

  const listRcId = {[nameAttribute]: `dl.${props.rcName || "list"}`};

  return (
    <>
      <DataTableBaseWrapper
        className={styles.root}
        id={`HOC-wrapper-${props.rcName || "list"}`}
        onScrollCapture={props.isLazyLoad ? onHandleScrollList : undefined}
        //$isWarning={isWarning}
        data-is-scrollable
        $isPaging={!props.isLazyLoad && !props.hidePagination}
      >
        <DataGrid
          className={styles.dataGrid}
          items={props.data || []}
          columns={columns}
          sortable
          getRowId={(item) => item.guid}
          resizableColumns
          columnSizingOptions={columnSizing}
          subtleSelection
          selectionMode={
            props.selectionMode === SelectionMode.Single
              ? "single"
              : "multiselect"
          }
        >
          <DataGridHeader>
            <DataGridRow
              selectionCell={{
                checkboxIndicator: {"aria-label": "Select all rows"},
              }}
            >
              {({renderHeaderCell, columnId}, dataGrid) =>
                dataGrid.resizableColumns ? (
                  <Menu openOnContext>
                    <MenuTrigger>
                      <DataGridHeaderCell
                        ref={(el) => (refMap.current[columnId] = el)}
                      >
                        {renderHeaderCell()}
                      </DataGridHeaderCell>
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem
                          onClick={dataGrid.columnSizing_unstable.enableKeyboardMode(
                            columnId
                          )}
                        >
                          Keyboard Column Resizing
                        </MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                ) : (
                  <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )
              }
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<any> className={styles.body} id={`HOC-wrapper-lazy-${props.rcName || "list"}`} {...listRcId}>
            {({item, rowId}) => (
              <DataGridRow<any>
                key={rowId}
                selectionCell={{
                  checkboxIndicator: {"aria-label": "Select row"},
                }}
              >
                {({renderCell}) => (
                  <DataGridCell>{renderCell(item)}</DataGridCell>
                )}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      </DataTableBaseWrapper>
      <Filter ref={refFilter} columns={props.columns} theme={props.theme}/>
    </>
  );
});

export default DataTable;